import React, { useEffect, useState } from 'react';
import { Alert, Modal, Title } from '@mantine/core';
import ABAApi from '../../../data/api/abaApi';
import ShiftSessionsApi from '../../../data/api/shiftSessionsApi';
import { IPayEvent } from '../../../types/aba';
import { IAppointment, IAppointmentDetail } from '../../../types/appointment/Appointment';

interface AppointmentWithPayment extends IAppointment<IAppointmentDetail> {
  payEventId?: string;
}
import { IPerson } from '../../../types/person/Person';
import { IShiftSession } from '../../../types/shifts/Shift';
import { AppointmentSelectionModal } from './AppointmentSelectionModal';

interface ShiftSessionWithName extends IShiftSession {
  shiftName: string;
}

interface ShiftPayEventModalProps {
  opened: boolean;
  onClose: () => void;
  session: ShiftSessionWithName | null;
  associationId: string;
  onPayEventCreated: (payEvent?: IPayEvent) => void;
  onSessionUpdated: (session: ShiftSessionWithName) => void;
}

const defaultPayEventName = (shiftName?: string, sessionStart?: Date) => {
  const today = new Date();
  const currentDate = today.toISOString().split('T')[0];

  if (shiftName && sessionStart) {
    const sessionDate = sessionStart.toISOString().split('T')[0];
    return `${currentDate} - ${shiftName} - ${sessionDate}`;
  }

  return `${currentDate} shift session pay event`;
};

export const ShiftPayEventModal: React.FC<ShiftPayEventModalProps> = ({
  opened,
  onClose,
  session,
  associationId,
  onPayEventCreated,
  onSessionUpdated
}) => {
  const [appointments, setAppointments] = useState<AppointmentWithPayment[]>([]);
  const [persons, setPersons] = useState<IPerson[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pay event form data
  const [payEventName, setPayEventName] = useState('');
  const [payEventDescription, setPayEventDescription] = useState('');

  // Load appointments when modal opens with a session
  useEffect(() => {
    if (opened && session) {
      loadAppointments();
    }
  }, [opened, session]);

  // Reset state when modal closes
  useEffect(() => {
    if (!opened) {
      setAppointments([]);
      setPersons([]);
      setPayEventName('');
      setPayEventDescription('');
      setError(null);
    }
  }, [opened]);

  const loadAppointments = async () => {
    if (!session) return;

    setAppointmentsLoading(true);
    setError(null);

    // Set the default pay event name for this session
    setPayEventName(defaultPayEventName(session.shiftName, new Date(session.start)));

    try {
      const response = await ShiftSessionsApi.getSessionAppointments(session._id);
      setAppointments(response.data || []);
      setPersons(response.persons || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load appointments');
    } finally {
      setAppointmentsLoading(false);
    }
  };


  const handlePaymentStatusUpdate = async (newStatus: 'unpaid' | 'partial' | 'paid') => {
    if (!session) return;

    setError(null);
    try {
      const updatedSession = await ShiftSessionsApi.updatePaymentStatus(session._id, newStatus);
      
      // Update the session with the new payment status
      const updatedSessionWithName: ShiftSessionWithName = {
        ...updatedSession,
        shiftName: session.shiftName
      };
      
      onSessionUpdated(updatedSessionWithName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update payment status');
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!session) return null;

  // Show loading state in modal
  if (appointmentsLoading) {
    return (
      <Modal
        opened={opened}
        onClose={handleClose}
        title={<Title order={3}>Loading Appointments - {session.shiftName}</Title>}
        size="md"
      >
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div>Loading appointments...</div>
        </div>
      </Modal>
    );
  }

  // Show error state in modal
  if (error && appointments.length === 0) {
    return (
      <Modal
        opened={opened}
        onClose={handleClose}
        title={<Title order={3}>Error Loading Appointments</Title>}
        size="md"
      >
        <Alert color="red" mb="md">
          <strong>Error:</strong> {error}
        </Alert>
      </Modal>
    );
  }

  return (
    <AppointmentSelectionModal
      opened={opened}
      onClose={handleClose}
      appointments={appointments}
      persons={persons}
      payEventName={payEventName}
      onPayEventNameChange={setPayEventName}
      payEventDescription={payEventDescription}
      onPayEventDescriptionChange={setPayEventDescription}
      onPayEventCreated={onPayEventCreated}
      associationId={associationId}
      sessionName={session.shiftName}
      sessionId={session._id}
      shiftId={session.shift}
      currentPaymentStatus={session.paymentStatus || 'unpaid'}
      onPaymentStatusUpdate={handlePaymentStatusUpdate}
      error={error}
    />
  );
};
